import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { DealApiService } from '../services/deal-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { DealDetailModalComponent } from '../deal-detail-modal/deal-detail-modal.component';
import {
  DealResponse,
  DealRequest,
  DealCycle,
  DealOrigin,
  DealBusinessType,
  DealPriority,
  DealSearchParams,
} from '../models/deal.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  catchError,
  debounceTime,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { DealContactApiService } from '../services/deal-contact-api.service';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../shared/services/permission.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { CompanyResponse } from '../../empresas/models/empresa.model';
import { CrmUserResponse } from '../../users/models/user.model';
import { QuoteFormModalService } from '../../quotes/services/quote-form-modal.service';

import {
  EntitySelectComponent,
  EntitySelectPage,
} from '../../../shared/components/entity-select/entity-select.component';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type ViewMode = 'dealCycle' | 'origin';

interface DealDisplay extends DealResponse {
  _amountDisplay: string;
  _priorityBg: string;
  _estimatedCloseFormatted: string;
  _isOverdue: boolean;
  _isInactive: boolean;
  _daysSinceUpdate: number;
  _cycleName: string;
  _originName: string;
}

interface KanbanColumn {
  id: number;
  name: string;
  color: string;
  deals: DealDisplay[];
}

interface FilterState {
  searchName: string;
  idDealCycle?: number;
  idDealOrigin?: number;
  idDealBusinessType?: number;
  idDealPriority?: number;
  minAmount?: number;
  maxAmount?: number;
  activeOnly?: boolean;
}

@Component({
  selector: 'app-deal-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    DealDetailModalComponent,
    DisableWithoutPermissionDirective,
    HasPermissionDirective,
    EntitySelectComponent,
    CountrySelectComponent,
  ],
  templateUrl: './deal-kanban.component.html',
  styleUrls: ['./deal-kanban.component.css'],
})
export class DealKanbanComponent implements OnInit, OnDestroy {
  viewMode: ViewMode = 'dealCycle';
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_negocios';
  private readonly destroy$ = new Subject<void>();

  /** Código ISO (minúscula) del país derivado del teléfono del contacto principal, para la bandera. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }
  /** Nombre del país derivado del teléfono (tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país (por prefijo telefónico del contacto principal). */
  readonly filterCountries = COUNTRIES_DATA;
  filtroPais = '';
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.filters.phonePrefix = code
      ? COUNTRIES_DATA.find(c => c.code === code)?.dialCode || undefined
      : undefined;
    this.applyFilters();
  }

  currentUser: number | undefined = undefined;

  filtroOwner =
    Number(localStorage.getItem(this.OWNER_FILTER_KEY)) || undefined;
  columns: KanbanColumn[] = [];
  loading = false;
  error = '';

  // Catálogos
  dealCycles: DealCycle[] = [];
  dealOrigins: DealOrigin[] = [];
  dealBusinessTypes: DealBusinessType[] = [];
  dealPriorities: DealPriority[] = [];

  companies: CompanyResponse[] = [];
  loadingCompanies = new Set<number>();

  dealUserResponsables: CrmUserResponse[] = [];
  foundUserResponsables = new Set<number>();
  listUsersResponsableWithDeals: CrmUserResponse[] = [];

  // Filtros
  filters: DealSearchParams = {
    businessId: 0,
    closedDateFrom: '',
    closedDateTo: '',
    searchName: '',
    idDealCycle: undefined,
    idDealOrigin: undefined,
    idDealBusinessType: undefined,
    idDealPriority: undefined,
    minAmount: undefined,
    maxAmount: undefined,
    activeOnly: true,
    archived: false,
  };
  filtroEstimatedCloseFrom = '';
  filtroEstimatedCloseTo = '';
  filtroCompanyName = '';

  /** Freno del filtro de texto. Ver {@link buscarTexto}. */
  private readonly busquedaTexto$ = new Subject<void>();
  filtroCompanyId: number | undefined;
  filtroNombre = '';
  filtroContacto = '';

  showFilters = false;

  // Modal de detalle
  showDetailModal = false;
  selectedDeal: DealResponse | null = null;

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'deal-kanban-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  constructor(
    private dealApi: DealApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private datetimeService: DatetimeService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private dealContactApi: DealContactApiService,
    private empresaApi: EmpresaApiService,
    private quoteFormModal: QuoteFormModalService,
    private permissions: PermissionService,
  ) {}

  /**
   * Un rol con solo "Ver" en Negocios entra a esta vista (es la de por defecto del módulo).
   * Sin esto podía arrastrar tarjetas: el backend rechazaba el cambio de etapa con 403 y la
   * tarjeta se quedaba donde el usuario la soltó, mintiendo sobre lo que se guardó.
   */
  get puedeEditar(): boolean {
    return this.permissions.canEdit('deals');
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.suscribirBusquedaTexto();

    // Al llegar con ?openDeal=<id> (p. ej. tras guardar el negocio desde la
    // página de formulario), abrir su modal de detalle sobre el kanban. El
    // detalle de un negocio es un modal, no una ruta propia — mismo patrón que
    // ?openTicket= en tickets y ?openLead= en leads.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const raw = params.get('openDeal');
        const id = raw ? Number(raw) : NaN;
        if (!Number.isNaN(id) && id > 0) this.abrirDetallePorId(id);
      });
  }

  private suscribirBusquedaTexto(): void {
    this.busquedaTexto$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.busquedaTexto$.complete();
  }

  /**
   * Freno del filtro de texto: cada tecla recargaba TODO el kanban (una consulta por
   * columna). Los selectores siguen aplicando al instante.
   */
  buscarTexto(): void {
    this.busquedaTexto$.next();
  }

  /**
   * Carga el negocio por id y abre su modal de detalle. Entrada de ?openDeal=<id>:
   * se pide al backend en vez de buscarlo en las columnas para que funcione
   * aunque el negocio no esté en la página cargada del kanban.
   */
  private abrirDetallePorId(id: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.dealApi
      .getDealById(id, businessId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deal) => {
          this.cargarCompany(deal.idCompany);
          this.selectedDeal = deal;
          this.showDetailModal = true;
        },
        error: () => {
          this.toast.error({
            title: 'No se pudo abrir el negocio',
            message: 'El negocio no existe o no tienes acceso.',
          });
        },
      });
  }

  private loadCatalogs(): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();

    const businessId = user?.businessId || 1;

    this.currentUser = user?.id;

    Promise.all([
      this.dealApi.getDealCycles().toPromise(),
      this.dealApi.getDealOrigins().toPromise(),
      this.dealApi.getDealBusinessTypes(businessId).toPromise(),
      this.dealApi.getDealPriorities(businessId).toPromise(),
      this.dealApi.getUsersByBusiness(businessId).toPromise(),
    ])
      .then(
        ([
          dealCycles,
          dealOrigins,
          dealBusinessTypes,
          dealPriorities,
          dealUsersResponsables,
        ]) => {
          this.dealCycles = dealCycles || [];
          this.dealOrigins = dealOrigins || [];
          this.dealBusinessTypes = dealBusinessTypes || [];
          this.dealPriorities = dealPriorities || [];
          this.dealUserResponsables = dealUsersResponsables || [];

          this.initializeColumns();
          this.loading = false;
        },
      )
      .catch((err) => {
        this.error = 'Error al cargar catálogos';
        this.loading = false;
      });
  }

  companyFilterSearch = (
    term: string,
    page: number,
  ): Observable<EntitySelectPage<CompanyResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.empresaApi
      .searchCompanies({ businessId, searchTerm: term, page, size: 10 })
      .pipe(
        map((r) => ({ items: r.content, totalPages: r.totalPages })),
        catchError(() => of({ items: [] as CompanyResponse[], totalPages: 0 })),
      );
  };

  companyFilterLabel = (c: CompanyResponse): string => c.commercialName;

  onCompanyFilterChange(id: number | null): void {
    this.filtroCompanyId = id ?? undefined;
    this.loadPage(0);
  }

  private initializeColumns(): void {
    this.columns = [];

    switch (this.viewMode) {
      case 'dealCycle':
        this.buildDealCycleColumns();
        break;
      case 'origin':
        this.buildOriginColumns();
        break;
    }

    this.loadPage(0);
  }

  private buildDealCycleColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-orange-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
    ];

    this.columns = this.dealCycles.map((cycle, index) => ({
      id: cycle.id,
      name: cycle.name,
      color: colors[index % colors.length],
      deals: [],
    }));

    // Agregar columna para deals sin ciclo
    this.columns.push({
      id: 0,
      name: 'Sin Etapa',
      color: 'bg-gray-500',
      deals: [],
    });
  }

  private buildOriginColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
    ];

    this.columns = this.dealOrigins.map((origin, index) => ({
      id: origin.id,
      name: origin.name,
      color: colors[index % colors.length],
      deals: [],
    }));

    // Agregar columna para deals sin origen
    this.columns.push({
      id: 0,
      name: 'Sin Origen',
      color: 'bg-gray-500',
      deals: [],
    });
  }

  private loadPage(page: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const params: DealSearchParams = {
      businessId,
      userPropietyId: this.filtroOwner ?? undefined,
      phonePrefix: this.filters.phonePrefix || undefined,
      searchName: this.filters.searchName || undefined,
      idCompany: this.filtroCompanyId ?? undefined,
      contactSearchTerm: this.filtroContacto || undefined,
      idDealCycle:
        this.viewMode === 'origin' && this.filters.idDealCycle !== undefined
          ? this.filters.idDealCycle
          : undefined,
      idDealBusinessType: this.filters.idDealBusinessType,
      idDealPriority: this.filters.idDealPriority,
      minAmount: this.filters.minAmount ?? undefined,
      maxAmount: this.filters.maxAmount ?? undefined,
      activeOnly: this.filters.activeOnly ?? undefined,
      closedOnly: this.filters.activeOnly === false ? true : undefined,
      idDealOrigin:
        this.viewMode === 'dealCycle' && this.filters.idDealOrigin !== undefined
          ? this.filters.idDealOrigin
          : undefined,
      page,
      size: this.pageSize,
      sortBy: 'datetimeCreated',
      sortDirection: 'DESC',
      closedDateFrom: this.datetimeService.dateToUtcIso(
        this.filters.closedDateFrom!,
      ),
      closedDateTo: this.datetimeService.dateToUtcIso(
        this.filters.closedDateTo!,
      ),
      estimatedCloseDateFrom: this.toDayStartIso(this.filtroEstimatedCloseFrom),
      estimatedCloseDateTo: this.toDayEndIso(this.filtroEstimatedCloseTo),
      archived: this.filters.archived ?? undefined,
    };

    this.columns.forEach((col) => (col.deals = []));

    this.dealApi.searchDeals(params).subscribe({
      next: (response) => {
        response.content.forEach((deal) => {
          this.cargarCompany(deal.idCompany);
          const col = this.findColumnForDeal(deal);
          if (col) col.deals.push(this.enrichDeal(deal));
        });
        this.usersHasDeals(response.content);
        this.globalPage = response.number ?? page;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar oportunidades';
        this.loading = false;
      },
    });
  }

  private toDayStartIso(dateStr: string): string | undefined {
    return dateStr ? `${dateStr}T00:00:00Z` : undefined;
  }

  private toDayEndIso(dateStr: string): string | undefined {
    return dateStr ? `${dateStr}T23:59:59Z` : undefined;
  }

  // Incidente 23 - Filtro Usuarios Responsables con Negocios
  private usersHasDeals(deals: DealResponse[]) {
    if (!deals) return;

    deals.forEach((deal) => {
      if (!deal.userPropietyId) return;
      const userResponsable = this.dealUserResponsables.find(
        (user) => deal.userPropietyId === user.id,
      );
      if (!userResponsable) return;

      if (this.foundUserResponsables.has(userResponsable.id)) return;

      this.foundUserResponsables.add(userResponsable.id);
      this.listUsersResponsableWithDeals.push(userResponsable);
    });
  }

  // Incidente 22 - Nombre de compañia sobre tarjeta
  resolverCompanies(companyId: number | undefined): string {
    if (!companyId) return 'Sin empresa asociada';

    const company = this.companies.find((c) => c.id === companyId);

    return company?.commercialName ?? 'Cargando...';
  }

  cargarCompany(companyId: number | undefined): void {
    if (!companyId) return;

    if (this.companies.some((c) => c.id === companyId)) {
      return;
    }

    if (this.loadingCompanies.has(companyId)) {
      return;
    }

    this.loadingCompanies.add(companyId);
    this.empresaApi.getCompanyById(companyId).subscribe({
      next: (company) => {
        this.companies.push(company);
      },
      error: () => {
        this.toast.error({
          title: 'Negocios',
          message: 'No se pudo cargar el nombre de la empresa asociada.',
        });
      },
    });
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.loadPage(page);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.applyFilters();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  private findColumnForDeal(deal: DealResponse): KanbanColumn | undefined {
    switch (this.viewMode) {
      case 'dealCycle':
        return this.columns.find((col) =>
          col.id === 0
            ? !deal.idDealCycle || deal.idDealCycle === 0
            : col.id === deal.idDealCycle,
        );
      case 'origin':
        return this.columns.find((col) =>
          col.id === 0
            ? !deal.idDealOrigin || deal.idDealOrigin === 0
            : col.id === deal.idDealOrigin,
        );
    }
  }

  changeViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.initializeColumns();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.loadPage(0);
  }

  setArchivesDeals() {
    this.filters.archived = !this.filters.archived;
    this.applyFilters();
  }

  setOwnerFilter(): void {
    this.filters.archived = false;
    if (this.filtroOwner === this.currentUser) {
      this.filtroOwner = undefined;
      localStorage.removeItem(this.OWNER_FILTER_KEY);
    } else {
      this.filtroOwner = this.currentUser;
      localStorage.setItem(this.OWNER_FILTER_KEY, String(this.currentUser));
    }

    this.applyFilters();
  }

  onOwnerFilterChange(id: number | null): void {
    this.filtroOwner = id ?? undefined;
    if (id) {
      localStorage.setItem(this.OWNER_FILTER_KEY, String(id));
    } else {
      localStorage.removeItem(this.OWNER_FILTER_KEY);
    }
    this.loadPage(0);
  }

  // displayText para el buscador de responsable cuando el valor se fija externamente
  get ownerFilterDisplay(): string {
    if (!this.filtroOwner) return '';
    const u = this.dealUserResponsables.find((x) => x.id === this.filtroOwner);
    return u ? `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim() : '';
  }

  userSearch = (
    term: string,
  ): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.dealApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.toLowerCase();
        const items = t
          ? users.filter((u) =>
              `${u.fullname} ${u.lastname ?? ''} ${u.mail}`
                .toLowerCase()
                .includes(t),
            )
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 })),
    );
  };

  userLabel = (u: CrmUserResponse): string =>
    `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  clearFilters(): void {
    this.filtroOwner = undefined;
    this.filtroCompanyId = undefined;
    this.filtroCompanyName = '';
    this.filtroEstimatedCloseFrom = '';
    this.filtroEstimatedCloseTo = '';
    this.filters = {
      businessId: 0,
      searchName: '',
      closedDateFrom: '',
      closedDateTo: '',
      idDealCycle: undefined,
      idDealOrigin: undefined,
      idDealBusinessType: undefined,
      idDealPriority: undefined,
      minAmount: undefined,
      maxAmount: undefined,
      activeOnly: true,
      archived: false,
      phonePrefix: undefined,
    };
    this.filtroPais = '';
    this.applyFilters();
  }

  get availableFilterFields(): { dealCycle: boolean; origin: boolean } {
    return {
      dealCycle: this.viewMode !== 'dealCycle',
      origin: this.viewMode !== 'origin',
    };
  }

  drop(event: CdkDragDrop<DealDisplay[]>, targetColumn: KanbanColumn): void {
    const deal = event.previousContainer.data[event.previousIndex];

    if (event.previousContainer === event.container) {
      // Reordenar dentro de la misma columna
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      // Encontrar la columna de origen ANTES de mover el deal
      const sourceColumn = this.columns.find(
        (col) => col.deals === event.previousContainer.data,
      );

      // Mover a otra columna
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      // Actualizar el deal en el backend
      this.updateDealCycle(deal, targetColumn, sourceColumn);
    }
  }

  private updateDealCycle(
    deal: DealDisplay,
    targetColumn: KanbanColumn,
    sourceColumn?: KanbanColumn,
  ): void {
    if (!deal.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const dealRequest: DealRequest = {
      name: deal.name,
      description: deal.description,
      amount: deal.amount,
      amountPerMonth: deal.amountPerMonth,
      amountPerYear: deal.amountPerYear,
      prefix: deal.prefix,
      idDealCycle: deal.idDealCycle,
      idCompany: deal.idCompany,
      idDealOrigin: deal.idDealOrigin,
      idDealBusinessType: deal.idDealBusinessType,
      idDealPriority: deal.idDealPriority,
      datetimeEstimatedClose: deal.datetimeEstimatedClose,
      businessId,
    };

    // Actualizar el campo correspondiente según el modo de vista
    switch (this.viewMode) {
      case 'dealCycle':
        dealRequest.idDealCycle = targetColumn.id === 0 ? 0 : targetColumn.id;
        break;
      case 'origin':
        dealRequest.idDealOrigin =
          targetColumn.id === 0 ? undefined : targetColumn.id;
        break;
    }

    this.dealApi.updateDeal(deal.id, dealRequest).subscribe({
      next: () => {
        deal.idDealCycle = dealRequest.idDealCycle;
        deal.idDealOrigin = dealRequest.idDealOrigin;
        const enriched = deal as DealDisplay;
        const cycle = this.dealCycles.find((c) => c.id === deal.idDealCycle);
        const origin = this.dealOrigins.find((o) => o.id === deal.idDealOrigin);
        enriched._cycleName = cycle?.name || 'Sin etapa';
        enriched._originName = origin?.name || 'Sin origen';
      },
      error: (err) => {
        this.confirmDialog.alert({
          title: 'Error',
          message:
            'Error al actualizar la oportunidad. Por favor, recargue la página.',
          variant: 'danger',
        });
        this.loadPage(this.globalPage);
      },
    });
  }

  getColumnIds(): string[] {
    return this.columns.map((col) => `col-${col.id}`);
  }

  nuevoDeal(): void {
    this.router.navigate(['/deals/crear']);
  }

  vistaLista(): void {
    this.router.navigate(['/deals/lista']);
  }

  editarDeal(deal: DealResponse, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/deals/editar', deal.id]);
  }

  async nuevaCopia(deal: DealResponse, event: Event): Promise<void> {
    event.stopPropagation();
    const ok = await this.confirmDialog.confirm({
      title: 'Crear copia',
      message: `Desea crear una copia de ${deal.name}`,
    });

    if (!ok) return;

    const newCopyDeal: DealResponse = { ...deal, name: `${deal.name} Copia` };

    this.dealApi.createDeal(newCopyDeal).subscribe({
      next: async (newDeal) => {
        const associationDealContact =
          deal.contacts?.map((contact) =>
            firstValueFrom(
              this.dealContactApi.createAssociation({
                idDeal: newDeal.id,
                idContact: contact.idContact,
                businessId: newDeal.businessId,
                idDealContactRole: contact.idDealContactRole,
                isPrimary: contact.isPrimary,
                notes: contact.notes || undefined,
              }),
            ),
          ) ?? [];

        await Promise.all(associationDealContact);

        this.toast.created('Nuevo negocio');
        this.router.navigate(['/deals/editar', newDeal.id]);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al crear la copia del negocio',
        });
      },
    });
  }

  archiveDeal(deal: DealResponse, event: Event): void {
    event.stopPropagation();
    if (!deal.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi.archiveDeal(deal.id, businessId).subscribe({
      next: () => {
        this.loadPage(this.globalPage);
        this.toast.success({
          title: 'Negocio',
          message: 'Negocio archivado',
        });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al archivar el negocio',
        });
      },
    });
  }

  unarchiveDeal(deal: DealResponse, event: Event): void {
    event.stopPropagation();
    if (!deal.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi.unarchiveDeal(deal.id, businessId).subscribe({
      next: () => {
        this.loadPage(this.globalPage);
        this.toast.success({
          title: 'Negocio',
          message: 'Negocio desarchivado',
        });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al desarchivar el negocio',
        });
      },
    });
  }

  async eliminarDeal(
    deal: DealResponse,
    column: KanbanColumn,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();

    if (!deal.id) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar oportunidad',
      message: `¿Está seguro de eliminar la oportunidad "${deal.name}"?`,
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi.deleteDeal(deal.id, businessId).subscribe({
      next: () => {
        const index = column.deals.findIndex((d) => d.id === deal.id);
        if (index !== -1) {
          column.deals.splice(index, 1);
        }
        this.toast.deleted('Negocio');
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el negocio',
        });
      },
    });
  }

  // Acción rápida: abrir el wizard de cotización con este negocio preseleccionado.
  nuevaCotizacion(deal: DealResponse, event: Event): void {
    event.stopPropagation();
    this.quoteFormModal.open({ dealId: deal.id });
  }

  verDetalle(deal: DealResponse): void {
    this.selectedDeal = deal;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedDeal = null;
    // Limpiar ?openDeal para poder reabrir el mismo negocio desde el buscador
    // (navegar dos veces a la misma URL no vuelve a emitir el query param).
    if (this.route.snapshot.queryParamMap.has('openDeal')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openDeal: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  onDealUpdated(): void {
    this.cerrarModal();
    this.loadPage(this.globalPage);
  }

  formatCurrency(amount?: number): string {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  getDealAmountDisplay(deal: DealResponse): string {
    const p = deal.prefix || '$';
    const fmt = (v: number) =>
      `${p} ${new Intl.NumberFormat('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v)}`;
    const parts: string[] = [];
    if (deal.amount) parts.push(fmt(deal.amount));
    if (deal.amountPerMonth) parts.push(`${fmt(deal.amountPerMonth)}/mes`);
    if (deal.amountPerYear) parts.push(`${fmt(deal.amountPerYear)}/año`);
    return parts.length > 0 ? parts.join(' · ') : '-';
  }

  getDealCycleName(id?: number): string {
    if (!id) return 'Sin etapa';
    const cycle = this.dealCycles.find((c) => c.id === id);
    return cycle?.name || '—';
  }

  getOriginName(id?: number): string {
    if (!id) return 'Sin origen';
    const origin = this.dealOrigins.find((o) => o.id === id);
    return origin?.name || '—';
  }

  isInactive(deal: DealResponse): boolean {
    const ref = deal.datetimeUpdated || deal.datetimeCreated;
    if (!ref) return false;
    const diffMs = Date.now() - new Date(ref).getTime();
    return diffMs > 7 * 24 * 60 * 60 * 1000;
  }

  getDaysSinceUpdate(deal: DealResponse): number {
    const ref = deal.datetimeUpdated || deal.datetimeCreated;
    if (!ref) return 0;
    return Math.floor(
      (Date.now() - new Date(ref).getTime()) / (1000 * 60 * 60 * 24),
    );
  }

  isOverdue(deal: DealResponse): boolean {
    if (!deal.datetimeEstimatedClose) return false;
    return new Date(deal.datetimeEstimatedClose) < new Date();
  }

  formatEstimatedClose(dateStr?: string): string {
    if (!dateStr) return '';
    return this.datetimeService.formatDateShort(dateStr);
  }

  getPriorityBgColor(hex?: string): string {
    if (!hex) return 'transparent';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},0.15)`;
  }

  private enrichDeal(deal: DealResponse): DealDisplay {
    const now = Date.now();
    const ref = deal.datetimeUpdated || deal.datetimeCreated;
    const diffMs = ref ? now - new Date(ref).getTime() : 0;
    const p = deal.prefix || '$';
    const fmt = (v: number) =>
      `${p} ${new Intl.NumberFormat('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v)}`;
    const amountParts: string[] = [];
    if (deal.amount) amountParts.push(fmt(deal.amount));
    if (deal.amountPerMonth)
      amountParts.push(`${fmt(deal.amountPerMonth)}/mes`);
    if (deal.amountPerYear) amountParts.push(`${fmt(deal.amountPerYear)}/año`);

    let priorityBg = 'transparent';
    if (deal.priorityColor) {
      const r = parseInt(deal.priorityColor.slice(1, 3), 16);
      const g = parseInt(deal.priorityColor.slice(3, 5), 16);
      const b = parseInt(deal.priorityColor.slice(5, 7), 16);
      priorityBg = `rgba(${r},${g},${b},0.15)`;
    }

    let estimatedCloseFormatted = '';
    if (deal.datetimeEstimatedClose) {
      estimatedCloseFormatted = this.datetimeService.formatDateShort(
        deal.datetimeEstimatedClose,
      );
    }

    const cycle = this.dealCycles.find((c) => c.id === deal.idDealCycle);
    const origin = this.dealOrigins.find((o) => o.id === deal.idDealOrigin);

    return {
      ...deal,
      _amountDisplay: amountParts.length > 0 ? amountParts.join(' · ') : '',
      _priorityBg: priorityBg,
      _estimatedCloseFormatted: estimatedCloseFormatted,
      _isOverdue: deal.datetimeEstimatedClose
        ? new Date(deal.datetimeEstimatedClose) < new Date()
        : false,
      _isInactive: ref ? diffMs > 7 * 24 * 60 * 60 * 1000 : false,
      _daysSinceUpdate: ref ? Math.floor(diffMs / (1000 * 60 * 60 * 24)) : 0,
      _cycleName: cycle?.name || 'Sin etapa',
      _originName: origin?.name || 'Sin origen',
    };
  }
}
